import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import CachedImage from './CachedImage';
import ViewerControls from './ViewerControls';
import useViewerNavigation from '../hooks/useViewerNavigation';
import { hasChapterAccess, isPatreonExclusive } from '../utils/patreonUtils';
import '../styles/Viewer.css';

// Import logo assets for random selection
import logoCreamsicle from '../assets/logos/Spirit Falls Logo Creamsicle.png';
import logoGreenGrey from '../assets/logos/Spirit Falls Logo GreenGrey.png';
import logoHalloween from '../assets/logos/Spirit Falls Logo Halloween.png';
import logoHoney from '../assets/logos/Spirit Falls Logo Honey.png';
import logoMonoPink from '../assets/logos/Spirit Falls Logo MonoPink.png';
import logoMushroom from '../assets/logos/Spirit Falls Logo Mushroom.png';
import logoOil from '../assets/logos/Spirit Falls Logo Oil.png';
import logoPastelBrooke from '../assets/logos/Spirit Falls Logo PastelBrooke.png';
import logoPastelLuna from '../assets/logos/Spirit Falls Logo PastelLuna.png';
import logoRedPurple from '../assets/logos/Spirit Falls Logo RedPurple.png';
import logoSpiritOcean from '../assets/logos/Spirit Falls Logo SpiritOcean.png';
import logoWanda from '../assets/logos/Spirit Falls Logo Wanda.png';
import logoMidnight from '../assets/logos/Spirit Falls Midgnight.png';

const STORAGE_KEYS = {
  viewMode: 'sf_viewMode_v2',
  fitMode: 'sf_fitMode_v2',
};

const VIEW_MODES = ['single', 'double'];
const FIT_MODES = ['fit-height', 'fit-width', 'fit-both'];

const alternativeLogos = [
  logoCreamsicle, logoGreenGrey, logoHalloween, logoHoney,
  logoMonoPink, logoMushroom, logoOil, logoPastelBrooke,
  logoPastelLuna, logoRedPurple, logoSpiritOcean, logoWanda, logoMidnight
];

/**
 * Build page entries from chapter data
 */
function buildPageEntries(chapter) {
  if (!chapter) return [];
  
  return (chapter.pageFiles || [])
    .filter(pageFile =>
      Array.isArray(pageFile.pages) &&
      pageFile.pages.length > 0 &&
      pageFile.filename
    )
    .map((pageFile, index) => {
      const pages = [...pageFile.pages].sort((a, b) => a - b);
      return {
        key: `${chapter.id}-${pages.join('-')}-${index}`,
        pages,
        image: `/chapterdata/${chapter.folder}/${pageFile.filename}`,
        isSpread: pages.length > 1,
      };
    });
}

/**
 * Build frames for different view modes
 */
function buildFrames(pageEntries) {
  // Single page mode: each entry is its own frame
  const single = pageEntries.map(entry => ({
    key: `${entry.key}-single`,
    pages: entry.pages,
    entries: [entry],
    hasSpread: entry.isSpread,
  }));

  // Double page mode: pair pages together
  const double = [];
  for (let i = 0; i < pageEntries.length; ) {
    const entry = pageEntries[i];
    
    // Spreads always get their own frame
    if (entry.isSpread) {
      double.push({
        key: `${entry.key}-spread`,
        pages: entry.pages,
        entries: [entry],
        hasSpread: true,
      });
      i += 1;
      continue;
    }

    // First page gets its own frame
    if (i === 0) {
      double.push({
        key: `${entry.key}-lead`,
        pages: entry.pages,
        entries: [entry],
        hasSpread: false,
      });
      i += 1;
      continue;
    }

    // Try to pair with next page
    const next = pageEntries[i + 1];
    if (next && !next.isSpread) {
      double.push({
        key: `${entry.key}-${next.key}-pair`,
        pages: [...entry.pages, ...next.pages],
        entries: [entry, next],
        hasSpread: false,
        isPaired: true,
      });
      i += 2;
    } else {
      double.push({
        key: `${entry.key}-solo`,
        pages: entry.pages,
        entries: [entry],
        hasSpread: false,
      });
      i += 1;
    }
  }

  return { single, double };
}

function ChapterViewer({ chapters, onSidebarToggle }) {
  const { chapterId: chapterIdParam, pageNumber: pageParam } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const chapterId = chapterIdParam ?? '';
  const parsedPage = Number.parseInt(pageParam ?? '', 10);
  const pageNumber = Number.isInteger(parsedPage) && parsedPage >= 0 ? parsedPage : 0;
  const accessToken = searchParams.get('access');

  // Find current chapter
  const chapter = useMemo(
    () => chapters.find(ch => ch.id === chapterId),
    [chapters, chapterId]
  );

  // Check access
  const hasAccess = useMemo(() => {
    if (!chapter) return false;
    return hasChapterAccess(chapter, accessToken);
  }, [chapter, accessToken]);

  // Get navigable chapters (exclude patreon-only unless current with access)
  const navigableChapters = useMemo(() => {
    return chapters.filter(ch => {
      if (isPatreonExclusive(ch)) {
        return ch.id === chapterId && hasAccess;
      }
      return true;
    });
  }, [chapters, chapterId, hasAccess]);

  // View state
  const [viewMode, setViewMode] = useState(() => {
    if (typeof window === 'undefined') return 'single';
    const stored = window.localStorage.getItem(STORAGE_KEYS.viewMode);
    return VIEW_MODES.includes(stored) ? stored : 'single';
  });

  const [fitMode, setFitMode] = useState(() => {
    if (typeof window === 'undefined') return 'fit-width';
    const stored = window.localStorage.getItem(STORAGE_KEYS.fitMode);
    return FIT_MODES.includes(stored) ? stored : 'fit-width';
  });

  const [controlsOpen, setControlsOpen] = useState(true);
  const [randomLogo] = useState(() => {
    const randomIndex = Math.floor(Math.random() * alternativeLogos.length);
    return alternativeLogos[randomIndex];
  });

  // Persist settings
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(STORAGE_KEYS.viewMode, viewMode);
    }
  }, [viewMode]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(STORAGE_KEYS.fitMode, fitMode);
    }
  }, [fitMode]);

  // Notify parent of sidebar state
  useEffect(() => {
    if (onSidebarToggle) {
      onSidebarToggle(controlsOpen);
    }
  }, [controlsOpen, onSidebarToggle]);

  // Build page entries and frames
  const pageEntries = useMemo(() => buildPageEntries(chapter), [chapter]);
  const framesByMode = useMemo(() => buildFrames(pageEntries), [pageEntries]);
  const frames = viewMode === 'double' ? framesByMode.double : framesByMode.single;
  
  const currentFrameIndex = frames.findIndex(frame => frame.pages.includes(pageNumber));
  const currentFrame = currentFrameIndex >= 0 ? frames[currentFrameIndex] : frames[0];

  // Navigation hook
  const {
    goToNextFrame,
    goToPreviousFrame,
    goToNextChapter,
    goToPreviousChapter,
    handleChapterChange,
    handlePageChange,
    handleViewModeChange,
    buildNavUrl
  } = useViewerNavigation({
    chapter,
    chapterId,
    pageNumber,
    viewMode,
    frames,
    framesByMode,
    currentFrameIndex,
    navigableChapters,
    accessToken
  });

  // Mode cycling
  const cycleViewMode = useCallback(() => {
    const currentIndex = VIEW_MODES.indexOf(viewMode);
    const nextMode = VIEW_MODES[(currentIndex + 1) % VIEW_MODES.length];
    setViewMode(nextMode);
    handleViewModeChange(nextMode);
  }, [viewMode, handleViewModeChange]);

  const cycleFitMode = useCallback(() => {
    const currentIndex = FIT_MODES.indexOf(fitMode);
    const nextMode = FIT_MODES[(currentIndex + 1) % FIT_MODES.length];
    setFitMode(nextMode);
  }, [fitMode]);

  // Scroll to top on page change in fit-width mode
  useEffect(() => {
    if (fitMode === 'fit-width') {
      window.scrollTo({ top: 0, behavior: 'instant' });
    }
  }, [pageNumber, fitMode]);

  // Redirect if invalid
  useEffect(() => {
    if (!chapter || !hasAccess) {
      navigate('/chapters', { replace: true });
      return;
    }

    const totalPages = chapter.totalPages ?? 1;
    if (pageNumber < 0 || pageNumber >= totalPages) {
      navigate(buildNavUrl(`/chapter/${chapterId}/page/0`), { replace: true });
    }
  }, [chapter, chapterId, navigate, pageNumber, hasAccess, buildNavUrl]);

  // Auto-correct invalid frame
  useEffect(() => {
    if (!chapter || frames.length === 0 || currentFrameIndex !== -1) {
      return;
    }
    const targetPage = Math.min(...frames[0].pages);
    if (targetPage !== pageNumber) {
      navigate(buildNavUrl(`/chapter/${chapterId}/page/${targetPage}`), { replace: true });
    }
  }, [chapter, chapterId, currentFrameIndex, frames, navigate, pageNumber, buildNavUrl]);

  // Canvas click navigation
  const canvasRef = useRef(null);
  const handleCanvasClick = useCallback((event) => {
    if (!currentFrame || !canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const midpoint = rect.left + rect.width / 2;

    if (event.clientX >= midpoint) {
      goToNextFrame();
    } else {
      goToPreviousFrame();
    }
  }, [currentFrame, goToNextFrame, goToPreviousFrame]);

  // Preload nearby pages
  const preloadSources = useMemo(() => {
    if (!frames.length || currentFrameIndex < 0) return [];
    
    const sources = [];
    const preloadRange = 3;
    
    for (let i = -preloadRange; i <= preloadRange; i++) {
      const targetIndex = currentFrameIndex + i;
      if (targetIndex >= 0 && targetIndex < frames.length && targetIndex !== currentFrameIndex) {
        const targetFrame = frames[targetIndex];
        if (targetFrame?.entries) {
          targetFrame.entries.forEach(entry => {
            if (entry.image) sources.push(entry.image);
          });
        }
      }
    }
    
    return sources;
  }, [frames, currentFrameIndex]);

  // Progress calculation
  const progress = frames.length > 0 && currentFrameIndex >= 0
    ? ((currentFrameIndex + 1) / frames.length) * 100
    : 0;

  // Render
  if (!chapter) {
    return (
      <div className="viewer-root empty">
        <div className="viewer-placeholder">Chapter not found.</div>
      </div>
    );
  }

  const isTwoUp = currentFrame?.entries?.length === 2;
  const containsSpread = currentFrame?.entries?.some(entry => entry.isSpread);

  return (
    <div className={`viewer-root ${viewMode} ${fitMode} ${controlsOpen ? 'controls-open' : ''}`}>
      <ViewerControls
        isOpen={controlsOpen}
        onToggle={() => setControlsOpen(open => !open)}
        chapter={chapter}
        navigableChapters={navigableChapters}
        currentChapterId={chapterId}
        frames={frames}
        currentFrame={currentFrame}
        currentFrameIndex={currentFrameIndex >= 0 ? currentFrameIndex : 0}
        viewMode={viewMode}
        fitMode={fitMode}
        onViewModeChange={cycleViewMode}
        onFitModeChange={cycleFitMode}
        onChapterChange={handleChapterChange}
        onPageChange={handlePageChange}
        onPreviousChapter={goToPreviousChapter}
        onNextChapter={goToNextChapter}
        onPreviousFrame={goToPreviousFrame}
        onNextFrame={goToNextFrame}
        randomLogo={randomLogo}
      />

      <div className="viewer-stage">
        <div
          ref={canvasRef}
          className={`page-strip ${isTwoUp ? 'two-up' : ''} ${containsSpread ? 'has-spread' : ''}`}
          onClick={currentFrame ? handleCanvasClick : undefined}
          role="presentation"
        >
          {currentFrame ? (
            currentFrame.entries.map((entry, index) => (
              <figure
                className={`page-frame${entry.isSpread ? ' spread' : ''}`}
                key={entry.key}
              >
                <CachedImage
                  className="page-img"
                  src={entry.image}
                  alt={`Chapter ${chapter.title} page ${entry.pages.join('-')}`}
                  draggable={false}
                  preloadSources={index === 0 ? preloadSources : []}
                />
              </figure>
            ))
          ) : (
            <div className="viewer-placeholder">Page not found.</div>
          )}
        </div>
      </div>

      {/* Progress bar */}
      <div className="viewer-progress">
        <div className="progress-track">
          <div className="progress-fill" style={{ width: `${progress}%` }} />
        </div>
      </div>
    </div>
  );
}

export default ChapterViewer;
